import React, { useMemo } from 'react';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { Home } from 'lucide-react';
import { extractPropertyName } from '../../../utils/propertyUtils';
import { usePropertyFilters } from '../../../contexts/PropertyFilterContext';

/**
 * Componente para exibir o título da propriedade
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.propertyData - Dados da propriedade
 * @returns {JSX.Element} Título da propriedade
 */
const PropertyTitle = ({ propertyData }) => {
  // Obter o contexto de filtros para que o título atualize quando os filtros mudam
  const { selectedCarCodes, selectedSigefCodes, selectedCafirCodes } = usePropertyFilters();

  // Usar useMemo para recalcular o nome apenas quando propertyData ou filtros mudarem
  const propertyName = useMemo(() => {
    // Se temos filtros ativos, podemos tentar encontrar o nome específico para o código filtrado
    let filteredPropertyName = 'Imóvel Rural';

    if (propertyData?.properties) {
      // Prioridade: SIGEF > SNCR > CAFIR conforme solicitado
      const props = propertyData.properties;

      // 1. Verificar SIGEF se há filtro de código ativo
      if (selectedSigefCodes.length > 0 && props.sigef) {
        const sigefCode = selectedSigefCodes[0]; // Pegar o primeiro código selecionado

        // Tentar encontrar o item SIGEF correspondente ao código filtrado
        if (Array.isArray(props.sigef)) {
          for (const sigefItem of props.sigef) {
            // Verificar se esse é o código filtrado
            if (
              sigefItem.codigo_imovel === sigefCode ||
              sigefItem.parcela_co === sigefCode ||
              sigefItem.codigo_imo === sigefCode
            ) {
              // Usar nome dessa parcela específica
              if (Array.isArray(sigefItem.details) && sigefItem.details.length > 0) {
                for (const detail of sigefItem.details) {
                  if (detail.nome_da_propriedade) return detail.nome_da_propriedade;
                }
              }
              if (sigefItem.nome_da_propriedade) return sigefItem.nome_da_propriedade;
              if (sigefItem.imovel_descricao) return sigefItem.imovel_descricao;
              if (sigefItem.denominacao) return sigefItem.denominacao;
            }
          }
        }
      }

      // 2. Verificar SNCR (não há filtro de código SNCR no momento, então só passamos para o padrão)

      // 3. Verificar CAFIR se há filtro de código ativo
      if (selectedCafirCodes.length > 0 && props.cafir) {
        const cafirCode = selectedCafirCodes[0]; // Pegar o primeiro código selecionado

        // Tentar encontrar o item CAFIR correspondente ao código filtrado
        if (props.cafir.imoveis && Array.isArray(props.cafir.imoveis)) {
          for (const imovel of props.cafir.imoveis) {
            // Verificar se esse é o código filtrado
            if (imovel.nirf === cafirCode) {
              // Usar nome desse imóvel específico
              if (imovel.nome_da_propriedade) return imovel.nome_da_propriedade;
              if (imovel.nome_imovel) return imovel.nome_imovel;
            }
          }
        }
      }

      // Se não encontrou nome específico para o código filtrado, usar o método padrão
      filteredPropertyName = extractPropertyName(propertyData);
    }

    return filteredPropertyName;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyData, selectedCarCodes, selectedSigefCodes, selectedCafirCodes]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
      <Home size={24} className="mr-2" />
      <Typography variant="h5" component="h1" fontWeight="bold">
        {propertyName}
      </Typography>
    </Box>
  );
};

export default PropertyTitle;
